import {getTestProps} from '@benchling/js-test-utils';
import {Box, Flex, SegmentedControl} from '@mantine/core';
import React, {useState} from 'react';
import isEqual from 'react-fast-compare';

import {stepTypeRequiresManualPreview} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettings';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {validatePipelineStepConfig} from 'pipelines/step-config-validation/validatePipelineStepConfig';
import {getInputStepDatasRecord} from 'pipelines/StepDataUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

import LiveStepDataPreview from './LiveStepDataPreview';
import ManualStepDataPreview from './ManualStepDataPreview';
import PersistedStepDataPreview from './PersistedStepDataPreview';
import {StepDataRole} from './PresentationalStepDataPreview';

export interface RightSidebarPreviewPaneProps {
  draftStep: PipelineStep;
  originalStep: PipelineStep;
  clientStateId: string;
}

function RightSidebarPreviewPaneContent({
  draftStep,
  originalStep,
  clientStateId,
  previewRole,
}: RightSidebarPreviewPaneProps & {previewRole: StepDataRole}): JSX.Element {
  if (previewRole === StepDataRole.INPUT) {
    return (
      <PersistedStepDataPreview
        step={draftStep}
        stepDataRole={StepDataRole.INPUT}
        clientStateId={clientStateId}
      />
    );
  }
  const {isComplete, errors} = validatePipelineStepConfig(
    draftStep.stepType,
    draftStep.stepVersion,
    draftStep.config ?? {},
    getInputStepDatasRecord(draftStep)
  );
  if (!isComplete || errors.length > 0) {
    const message = !isComplete ? 'Step config is incomplete' : 'Step config is invalid';
    return (
      <Box mx="md" my="sm">
        <InlineBanner bannerType={InlineBannerType.WARNING} text={message} />
      </Box>
    );
  }

  if (isEqual(draftStep, originalStep)) {
    // The saved step is unchanged, so show its persisted outputs rather than a live preview.
    return <PersistedStepDataPreview step={originalStep} stepDataRole={StepDataRole.OUTPUT} />;
  }
  if (stepTypeRequiresManualPreview(draftStep.stepType, draftStep.stepVersion)) {
    return <ManualStepDataPreview draftStep={draftStep} clientStateId={clientStateId} />;
  }
  return <LiveStepDataPreview draftStep={draftStep} clientStateId={clientStateId} />;
}

/**
 * The preview panel shown when the step config sidebar is expanded.
 *
 * `RightSidebarPreviewPane` routes to the appropriate `{Persisted,Live,Manual}StepDataPreview` component,
 * depending on if previews should be live-updating or manually triggered, or of the step's already persisted
 * inputs or outputs.
 *
 * Regardless of querying method, `{Persisted,Live,Manual}StepDataPreview` all wrap the same presentational
 * `<PresentationalStepDataPreview>` component.
 *
 * The basic structure of the preview pane is:
 *
 *  <RightSidebarPreviewPane>
 *      <{Persisted,Live,Manual}StepDataPreview>
 *          <PresentationalStepDataPreview>
 *              <StepDataGroup>
 *                  <SingleStepDataPreview/>
 *                  <SingleStepDataPreview/>
 *              </StepDataGroup>
 *              <StepDataGroup>
 *                  <SingleStepDataPreview/>
 *                  <SingleStepDataPreview/>
 *              </StepDataGroup>
 *          </PresentationalStepDataPreview>
 *      </{Persisted,Live,Manual}StepDataPreview>
 *  </RightSidebarPreviewPane>
 */
export default function RightSidebarPreviewPane({
  draftStep,
  originalStep,
  clientStateId,
}: RightSidebarPreviewPaneProps): JSX.Element {
  const [previewRole, setPreviewRole] = useState<StepDataRole>(StepDataRole.OUTPUT);

  return (
    <>
      <Box mb={0} mx="md" sx={{flexShrink: 0}}>
        <SegmentedControl
          fullWidth={true}
          size="xs"
          value={StepDataRole[previewRole]}
          onChange={(value) => {
            setPreviewRole(StepDataRole[value as keyof typeof StepDataRole]);
          }}
          data={[
            {label: 'Inputs', value: StepDataRole[StepDataRole.INPUT]},
            {label: 'Outputs', value: StepDataRole[StepDataRole.OUTPUT]},
          ]}
          {...getTestProps({component: 'RightSidebarPreviewPane', element: 'previewModeToggle'})}
        />
      </Box>
      <Flex direction="column" sx={{flex: 1, minHeight: 0}}>
        <RightSidebarPreviewPaneContent
          draftStep={draftStep}
          originalStep={originalStep}
          clientStateId={clientStateId}
          previewRole={previewRole}
        />
      </Flex>
    </>
  );
}
